import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Sprout, ShoppingBag, MapPin, Eye, Edit3, Clock, Package, PackageX, PackageCheck } from 'lucide-react';
import { toast } from 'sonner';
import Avatar from '@/components/Avatar';

/**
 * Facebook-style "wall" that lists the current user's own content chronologically.
 * - Farmers see their produce posts (available + sold)
 * - Buyers see their requirement posts (open + closed)
 * - Farmer-buyers see both, sorted by created_at desc
 */
export default function MyPostsFeed() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const calls = [api.get('/requirements/mine')];
      if (user.role === 'farmer') {
        calls.push(api.get(`/farmer/${user._id}/produce`));
      }
      const results = await Promise.allSettled(calls);
      const reqs = results[0].status === 'fulfilled' ? (results[0].value.data.posts || []) : [];
      const prods = results[1]?.status === 'fulfilled' ? (results[1].value.data.posts || []) : [];

      const merged = [
        ...reqs.map(p => ({ ...p, __type: 'requirement' })),
        ...prods.map(p => ({ ...p, __type: 'produce' })),
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setItems(merged);
    } catch {
      toast.error('Failed to load your posts');
    } finally {
      setLoading(false);
    }
  }, [api, user]);

  useEffect(() => { load(); }, [load]);

  const timeAgo = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diff < 60) return `${Math.max(diff, 1)}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const emptyText = user?.role === 'farmer'
    ? "You haven't posted any produce or requirements yet."
    : "You haven't posted any requirements yet.";

  return (
    <div className="space-y-3" data-testid="my-posts-feed">
      {loading ? (
        Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-32 rounded-2xl bg-[#EFF2F7] animate-pulse" />
        ))
      ) : items.length === 0 ? (
        <div className="text-center py-14 bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF]">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#EFF2F7] flex items-center justify-center mb-3">
            <Sprout className="w-6 h-6 text-[#0B4F3F]/60" />
          </div>
          <p className="text-[13px] font-semibold text-[#0B1620]">Nothing here yet</p>
          <p className="text-xs text-[#5B6675] mt-1 max-w-[240px] mx-auto">{emptyText}</p>
          <button
            data-testid="empty-post-cta"
            onClick={() => navigate('/create-post')}
            className="mt-4 inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-[#0B4F3F] text-[#F6F8FB] text-sm font-semibold hover:bg-[#083D31] transition-colors"
          >
            Create your first post
          </button>
        </div>
      ) : (
        items.map((p) => {
          const isProduce = p.__type === 'produce';
          const image = isProduce ? (p.images?.[0]) : p.image;
          const isActive = isProduce ? p.is_available : p.is_open;
          const detailPath = isProduce ? `/produce/${p.id}` : `/requirement/${p.id}`;
          const editPath = isProduce ? `/edit-produce/${p.id}` : `/edit-requirement/${p.id}`;
          return (
            <div
              key={`${p.__type}-${p.id}`}
              data-testid={`my-post-${p.__type}-${p.id}`}
              className="bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] overflow-hidden"
            >
              {/* FB-style header row: avatar + name + timestamp + type badge */}
              <div className="flex items-center gap-3 px-4 pt-4">
                <Avatar name={user?.name} src={user?.avatar} size={36} className="ring-1 ring-[#E4E8EF]" />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-[#0B1620] truncate leading-tight">{user?.name || 'You'}</p>
                  <div className="flex items-center gap-1.5 text-[10.5px] text-[#5B6675] leading-tight mt-0.5">
                    <span className="flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> {timeAgo(p.created_at)}</span>
                    <span className="opacity-40">·</span>
                    <span
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                        isProduce
                          ? 'bg-[#0B4F3F]/10 text-[#0B4F3F]'
                          : 'bg-[#F59E0B]/10 text-[#B45309]'
                      }`}
                    >
                      {isProduce ? <Sprout className="w-2.5 h-2.5" /> : <ShoppingBag className="w-2.5 h-2.5" />}
                      {isProduce ? 'Produce' : 'Requirement'}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid={`my-post-edit-${p.__type}-${p.id}`}
                  onClick={(e) => { e.stopPropagation(); navigate(editPath); }}
                  aria-label="Edit"
                  className="w-8 h-8 rounded-full bg-[#EFF2F7] hover:bg-[#E4E8EF] flex items-center justify-center flex-shrink-0"
                >
                  <Edit3 className="w-3.5 h-3.5 text-[#0B1620]" />
                </button>
              </div>

              {/* Body: title + short meta */}
              <button
                type="button"
                onClick={() => navigate(detailPath)}
                className="w-full text-left px-4 py-3 hover:bg-[#EFF2F7]/50 transition-colors"
              >
                <p className="text-[15px] font-semibold text-[#0B1620] font-['Bricolage_Grotesque'] tracking-tight leading-snug line-clamp-2">
                  {p.title}
                </p>
                {p.description && (
                  <p className="text-[12.5px] text-[#5B6675] mt-1 line-clamp-2 leading-relaxed">{p.description}</p>
                )}
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                    isActive
                      ? 'bg-[#15803D]/10 text-[#15803D]'
                      : 'bg-[#5B6675]/10 text-[#5B6675]'
                  }`}>
                    {isProduce
                      ? (isActive ? <><PackageCheck className="w-2.5 h-2.5 inline mr-0.5" /> Available</> : <><PackageX className="w-2.5 h-2.5 inline mr-0.5" /> Sold out</>)
                      : (isActive ? 'Open' : 'Closed')}
                  </span>
                  <span className="text-[11px] text-[#5B6675] uppercase tracking-wider">{p.category}</span>
                  <span className="text-[11px] font-bold text-[#0B4F3F] font-['Bricolage_Grotesque']">
                    ₹{p.price_per_unit}<span className="font-normal text-[#5B6675]">/{p.unit}</span>
                  </span>
                  <span className="text-[11px] text-[#5B6675]">· {p.quantity} {p.unit}</span>
                </div>
              </button>

              {/* Media */}
              {image && (
                <button
                  type="button"
                  onClick={() => navigate(detailPath)}
                  className="block w-full bg-[#EFF2F7]"
                  aria-label="Open post"
                >
                  <img
                    src={image}
                    alt=""
                    loading="lazy"
                    className="w-full max-h-72 object-cover"
                  />
                </button>
              )}

              {/* Footer row: location + views */}
              <div className="px-4 py-2.5 flex items-center justify-between text-[10.5px] text-[#5B6675] border-t border-[#E4E8EF]/60">
                <span className="flex items-center gap-1">
                  {p.location ? (
                    <><MapPin className="w-3 h-3" /> {p.location}</>
                  ) : (
                    <><Package className="w-3 h-3" /> {p.quantity} {p.unit}</>
                  )}
                </span>
                <span className="flex items-center gap-1 tabular-nums">
                  <Eye className="w-3 h-3" /> {p.views || 0}
                </span>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
